import { DateCell, FileViewCell, RawTextCell, TooltipCell } from '@/components/Table/components/Cell'
import { TenderDocumentStatus } from '@eapproval/offer-shared'
import {
  CostBudgetWorkflowInitiatorType,
  SourcingDecisionWorkflowStatus,
} from '@/features/offerCostBudgets/constants/offerCostBudget.constant'
import { createOptions } from '@/utils/arrayHelper'
import { createICS } from '@/utils/outlook'
import { camelToWords, formatCurrencyAmount, numberWithCommas } from '@/utils/stringHelper'
import { Button, Flex } from '@chakra-ui/react'
import { createColumnHelper } from '@tanstack/react-table'
import React from 'react'
import { MdDeleteOutline, MdOutlineCheckCircle } from 'react-icons/md'
import { Currency } from '../../constants'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'

export const getOfferSourcingDecisionTableColumns = ({
  salesUserOptions,
  userOptions,
  sourcingUserOptions,
  vendorOptions,
}) => {
  const columnHelper = createColumnHelper()

  return [
    columnHelper.display({
      id: 'action',
      size: 80,
      enableHiding: false,
      enableColumnFilter: false,
      cell: ({ row: { original } }) => {
        return (
          <Button
            size="sm"
            colorScheme="blue"
            onClick={(e) => {
              e.stopPropagation()

              createICS(original.dueDate, original.projectName, original._id, original.rfqNumber, false)
            }}
          >
            Outlook
          </Button>
        )
      },
    }),
    columnHelper.accessor('rfqNumber', {
      header: () => <span>RFQ #</span>,
      cell: RawTextCell,
    }),
    columnHelper.accessor('projectName', {
      cell: RawTextCell,
    }),
    columnHelper.accessor('customer', {
      cell: RawTextCell,
    }),
    columnHelper.accessor('subSystemName', {
      cell: RawTextCell,
    }),
    columnHelper.accessor('scopeOfSupply', {
      cell: RawTextCell,
    }),
    columnHelper.accessor('salesResponsible', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: salesUserOptions,
      },
    }),
    columnHelper.accessor('creator', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: userOptions,
      },
    }),
    columnHelper.accessor('dueDate', {
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
      },
    }),
    columnHelper.accessor('procurementResponsible', {
      header: () => <span>Procurement Responsible</span>,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: sourcingUserOptions,
      },
    }),
    columnHelper.accessor('hasCommented', {
      header: () => <span>Procurement Commented</span>,

      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
      },
    }),
    columnHelper.accessor('numberOfContractorPosted', {
      header: () => <span># of Contractor Posted</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.RANGE,
      },
    }),
    columnHelper.accessor('numberOfUpload', {
      header: () => <span># of Contractor Uploaded</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.RANGE,
      },
    }),

    columnHelper.accessor('PM040SourcingSelection', {
      header: () => <span>PM040 Sourcing Selection</span>,
      cell: ({ getValue }) => {
        const value = getValue()
        return <TooltipCell text={value ? value.replace('selected-', '') : value} />
      },
      meta: {
        filterVariant: FilterType.SELECT,
        filterOptions: [
          { label: 'Selected', value: 'selected' },
          { label: 'Not Selected', value: '(-)' },
          { label: 'Reference Only', value: 'reference' },
          ...vendorOptions,
        ],
      },
    }),
    columnHelper.accessor('PM040TechnicalSelection', {
      header: () => <span>PM040 Technical Selection</span>,
      cell: ({ getValue }) => {
        const value = getValue()
        return <TooltipCell text={camelToWords(value ? value.replace('selected-', '') : value)} />
      },

      meta: {
        filterVariant: FilterType.SELECT,
        filterOptions: [
          { label: 'Selected', value: 'selected' },
          { label: 'Not Selected', value: '(-)' },
          { label: 'Reference Only', value: 'reference' },

          ...vendorOptions,
        ],
      },
    }),

    columnHelper.accessor('PM100TechnicalSelection', {
      header: () => <span>PM100 Technical Selection</span>,
      cell: ({ getValue }) => {
        const value = getValue()
        return <TooltipCell text={camelToWords(value ? value.replace('selected-', '') : value)} />
      },

      meta: {
        filterVariant: FilterType.SELECT,
        filterOptions: [
          { label: 'Selected', value: 'selected' },
          { label: 'Not Selected', value: '(-)' },
          { label: 'Reference Only', value: 'reference' },

          ...vendorOptions,
        ],
      },
    }),
    columnHelper.accessor('quoteAmount', {
      header: () => <span>Latest Quote Amount</span>,
      cell: ({ getValue, row: { original } }) => {
        const value = getValue()
        const tooltip = original?.lastUploadVendor ? `Last uploaded by ${original?.lastUploadVendor}` : ''
        return (
          <TooltipCell text={formatCurrencyAmount(value ?? 0, original?.latestQuoteCurrency ?? '')} tooltip={tooltip} />
        )
      },

      meta: {
        filterVariant: FilterType.RANGE,
      },
    }),
    columnHelper.accessor('noticedProcurementOfficer', {
      header: () => <span>Noticed Procurement Officer</span>,

      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
      },
    }),
    columnHelper.accessor('requireProcurementOfficer', {
      header: () => <span>Required Procurement Officer</span>,

      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
      },
    }),
    columnHelper.accessor('updatedAt', {
      header: () => <span>Last Update</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
      },
    }),
    columnHelper.accessor('internalRemark', {
      cell: RawTextCell,
    }),
    columnHelper.accessor('latestQuoteCurrency', {
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(Currency).map((currency) => ({
          label: currency,
          value: currency,
        })),
      },
    }),

    columnHelper.accessor('costBudgetLargerThanThreshold', {
      header: () => <span>Cost Budget {'>'}100K</span>,

      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
      },
    }),
    columnHelper.accessor('workflowStatus', {
      header: () => <span>Workflow Status</span>,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(SourcingDecisionWorkflowStatus).map((status) => ({
          label: status,
          value: status,
        })),
      },
    }),
    columnHelper.accessor('addendumStatus', {
      header: () => <span>Addendum Status</span>,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(TenderDocumentStatus).map((status) => ({
          label: status,
          value: status,
        })),
      },
    }),
    columnHelper.accessor('CTG', {
      header: () => <span>CTG</span>,
      cell: ({ getValue }) => <TooltipCell text={numberWithCommas(getValue())} />,

      meta: {
        filterVariant: FilterType.RANGE,
      },
    }),
    columnHelper.accessor('CNI', {
      header: () => <span>CNI</span>,
      cell: ({ getValue }) => <TooltipCell text={numberWithCommas(getValue())} />,

      meta: {
        filterVariant: FilterType.RANGE,
      },
    }),
    columnHelper.accessor('appliedSingleSource', {
      header: () => <span>Apply Single Source Procurement ? </span>,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
      },
    }),
    columnHelper.accessor('offerStatus', {
      header: () => <span>Offer Status</span>,
      cell: RawTextCell,
      //TODO: 3/2/2025
    }),
  ]
}
